import React, { useEffect, useState } from "react";
import { api, formatErr, fmtMoney } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { Activity, Database, ShieldAlert, Users, Cloud, TrendingUp, AlertTriangle, CheckCircle2, Clock, KeyRound } from "lucide-react";
import { Link } from "react-router-dom";

export default function SystemHealth() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get("/system/health"); setData(r.data); }
    catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="p-8 text-center text-muted-foreground">جاري تحميل بيانات الصحة...</div>;

  const bStale = data.last_backup?.stale;
  const b = data.last_backup;
  const has2FA = data.two_fa_coverage?.enabled_users > 0;
  const twofaPct = data.two_fa_coverage?.pct || 0;

  return (
    <div className="space-y-6" data-testid="system-health-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-bold flex items-center gap-2"><Activity className="w-7 h-7 text-primary" /> الصحة العامة للنظام</h1>
          <p className="text-sm text-muted-foreground mt-1">نظرة شاملة على السلامة الأمنية والتشغيلية للبرنامج</p>
        </div>
        <Button variant="outline" onClick={load} disabled={loading}>{loading ? "جاري..." : "تحديث"}</Button>
      </div>

      {/* Alerts row */}
      {(bStale || data.suspicious_deletes_24h > 3 || data.failed_logins_24h > 20 || data.expired_subscriptions.length > 0) && (
        <Card className="p-4 border-destructive bg-destructive/5" data-testid="health-alerts">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-destructive shrink-0" />
            <div className="flex-1 space-y-1 text-sm">
              <div className="font-bold text-destructive">تنبيهات تحتاج انتباهك</div>
              {bStale && <div>• آخر نسخة احتياطية أقدم من 30 ساعة ({b?.hours_ago}h)</div>}
              {data.suspicious_deletes_24h > 3 && <div>• {data.suspicious_deletes_24h} عملية حذف في آخر 24 ساعة — تحقق من سجل التدقيق</div>}
              {data.failed_logins_24h > 20 && <div>• {data.failed_logins_24h} محاولة دخول فاشلة اليوم — احتمال محاولات اختراق</div>}
              {data.expired_subscriptions.length > 0 && <div>• {data.expired_subscriptions.length} شركة اشتراكها منتهي</div>}
            </div>
          </div>
        </Card>
      )}

      {/* Main KPI grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card className="p-4" data-testid="health-backup">
          <div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">آخر نسخة احتياطية</span><Database className="w-4 h-4 text-primary" /></div>
          {b ? (
            <div className="mt-2">
              <div className="text-xl font-bold tabular-nums">{b.hours_ago}h</div>
              <div className="text-[11px] text-muted-foreground truncate">{b.filename}</div>
              <div className="text-[10px] mt-1 flex items-center gap-1">
                {b.cloud_uploaded ? <><Cloud className="w-3 h-3 text-sky-600" /> <span className="text-sky-700">مرفوعة سحابياً</span></> : <span className="text-amber-700">محلية فقط</span>}
              </div>
            </div>
          ) : <div className="mt-2 text-sm text-destructive">لا توجد نسخة</div>}
        </Card>

        <Card className="p-4" data-testid="health-failed-logins">
          <div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">محاولات فاشلة (24 ساعة)</span><ShieldAlert className={`w-4 h-4 ${data.failed_logins_24h > 20 ? "text-destructive" : "text-muted-foreground"}`} /></div>
          <div className={`mt-2 text-2xl font-bold tabular-nums ${data.failed_logins_24h > 20 ? "text-destructive" : ""}`}>{data.failed_logins_24h}</div>
          <div className="text-[10px] text-muted-foreground mt-1">Rate limit: 5 محاولات / 5 دقائق / IP</div>
        </Card>

        <Card className="p-4" data-testid="health-suspicious">
          <div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">حذف مشبوه (24س)</span><AlertTriangle className={`w-4 h-4 ${data.suspicious_deletes_24h > 3 ? "text-amber-600" : "text-muted-foreground"}`} /></div>
          <div className="mt-2 text-2xl font-bold tabular-nums">{data.suspicious_deletes_24h}</div>
          <div className="text-[10px] text-muted-foreground mt-1">فواتير · أصناف · أدوار</div>
        </Card>

        <Card className="p-4" data-testid="health-2fa">
          <div className="flex items-center justify-between"><span className="text-xs uppercase text-muted-foreground">تغطية 2FA</span><KeyRound className={`w-4 h-4 ${twofaPct >= 50 ? "text-emerald-600" : "text-amber-600"}`} /></div>
          <div className={`mt-2 text-2xl font-bold tabular-nums ${twofaPct >= 50 ? "text-emerald-600" : "text-amber-600"}`}>{twofaPct}%</div>
          <div className="text-[10px] text-muted-foreground mt-1">{data.two_fa_coverage?.enabled_users}/{data.two_fa_coverage?.total_users} مستخدم</div>
        </Card>
      </div>

      {/* Subscriptions */}
      <div className="grid md:grid-cols-2 gap-4">
        <Card className="p-4" data-testid="health-expired">
          <div className="flex items-center gap-2 mb-3 font-bold"><AlertTriangle className="w-4 h-4 text-destructive" /> اشتراكات منتهية ({data.expired_subscriptions.length})</div>
          {data.expired_subscriptions.length === 0 ? <div className="text-xs text-muted-foreground">لا توجد ✓</div> : (
            <div className="space-y-1.5">
              {data.expired_subscriptions.slice(0, 5).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm p-2 bg-destructive/5 rounded">
                  <span className="font-semibold">{s.name}</span>
                  <span className="text-xs text-destructive">منذ {s.days_overdue} يوم</span>
                </div>
              ))}
            </div>
          )}
        </Card>

        <Card className="p-4" data-testid="health-expiring">
          <div className="flex items-center gap-2 mb-3 font-bold"><Clock className="w-4 h-4 text-amber-600" /> اشتراكات قاربت على الانتهاء ({data.expiring_soon.length})</div>
          {data.expiring_soon.length === 0 ? <div className="text-xs text-muted-foreground">لا توجد ✓</div> : (
            <div className="space-y-1.5">
              {data.expiring_soon.slice(0, 5).map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm p-2 bg-amber-50 rounded">
                  <span className="font-semibold">{s.name}</span>
                  <span className="text-xs text-amber-700">{s.days_left} يوم متبقي</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>

      {/* Audit summary */}
      <Card className="p-4" data-testid="health-audit-summary">
        <div className="flex items-center gap-2 mb-3 font-bold"><TrendingUp className="w-4 h-4 text-primary" /> نشاط النظام — آخر 7 أيام</div>
        {data.audit_summary_7d.length === 0 ? <div className="text-xs text-muted-foreground">لا نشاط مسجل</div> : (
          <div className="space-y-1">
            {data.audit_summary_7d.map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm p-2 bg-accent/40 rounded">
                <span className="font-mono text-xs">{a.action}</span>
                <span className="font-bold tabular-nums">{a.count}</span>
              </div>
            ))}
          </div>
        )}
        <div className="mt-3 text-xs">
          <Link to="/audit-log" className="text-primary hover:underline">عرض السجل الكامل →</Link>
        </div>
      </Card>

      <div className="text-[10px] text-muted-foreground text-end">تم توليد التقرير: {new Date(data.generated_at).toLocaleString("ar-EG")}</div>
    </div>
  );
}
